import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ContentWrapperOverride, TeamMatchupWrapper, ScoreWrapper, TeamWrapper, TeamScoreWrapper, GameInfoWrapper, InfoWrapper, SubLabelWrapper, StatusLayerContainer } from './VerticalGame.style';
import { TeamLogoTypes } from '@mlb/react-patterns/v4/components/TeamLogo/teamLogo.types';
import Team from '@mlb/react-patterns/v4/components/Team/team';
import getScoresData from 'bdata-sns-shared/utils/getScoresData';
import { getVerticalGameStateLabel } from '../utils/getVerticalGameStateLabel';
import GameDataLayer from './GameDataLayer/GameDataLayer';
import { TagOverride, StatusLayerWrapper } from './StatusLayer/StatusLayer.style';
import getNotableGameLabel from 'bdata-sns-shared/utils/getNotableGameLabel';
import LabelTypes from 'bdata-sns-shared/types/LabelTypes';
import { getLocalTimezone } from 'bdata-sns-shared/store/selectors/getLocalTimezone';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';

const nameFontSize = 12;
const logoSize = 32;
const { SPOT } = TeamLogoTypes;
const { FULL } = LabelTypes;
const homeClassOverride = FULL + ' right';

const VerticalGame = ({
    disableCell,
    flags,
    dictionary,
    mode,
    status,
    teams,
    linescore,
    gameState,
    gameDate,
    gameNumber,
    isLoading = true,
    enabledInningState,
    outsWidth,
    basepathsWidth,
    isActiveGame,
    tz,
    env
}) => {
    const homeData = teams?.home;
    const awayData = teams?.away;
    const {
        homeScore,
        awayScore
    } = getScoresData(linescore, teams);
    const localTimezone = useSelector(getLocalTimezone);
    const { gameBaseLabel, gameSubLabel } = getVerticalGameStateLabel({
        gameState,
        dictionary,
        gameDate,
        tz,
        gameNumber,
        status,
        linescore,
        localTimezone
    });
    const { awayTeamNoHitter, awayTeamPerfectGame, homeTeamNoHitter, homeTeamPerfectGame } = flags || {};
    const awayNotableGameLabelFull = getNotableGameLabel({ perfectGame: awayTeamPerfectGame, noHitter: awayTeamNoHitter }, dictionary, FULL);
    const homeNotableGameLabelFull = getNotableGameLabel({ perfectGame: homeTeamPerfectGame, noHitter: homeTeamNoHitter }, dictionary, FULL);

    return (
        <ContentWrapperOverride
            disableCell={disableCell}
            className={'content-wrapper'}
        >
            <StatusLayerContainer>
                <StatusLayerWrapper $mode={mode} $visibility={!!awayNotableGameLabelFull}>
                    { !isLoading
                        ? (awayNotableGameLabelFull) && <TagOverride
                            $mode={mode}
                            label={ awayNotableGameLabelFull }
                            color={ 'link-blue' }
                            className={FULL}
                            dataAttr={{
                                'data-mlb-test': 'notableGameLabelFull',
                                'data-test-id': 'notableGameLabelFull'
                            }}
                        />
                        : <Skeleton height='10px' width='30px' margin />}
                </StatusLayerWrapper>

                <StatusLayerWrapper $mode={mode} $visibility={!!homeNotableGameLabelFull}>
                    { !isLoading
                        ? (homeNotableGameLabelFull) && <TagOverride
                            $mode={mode}
                            label={ homeNotableGameLabelFull }
                            color={ 'link-blue' }
                            className={homeClassOverride}
                            dataAttr={{
                                'data-mlb-test': 'notableGameLabelFull',
                                'data-test-id': 'notableGameLabelFull'
                            }}
                        />
                        : <Skeleton height='10px' width='30px' margin />}
                </StatusLayerWrapper>
            </StatusLayerContainer>

            <TeamMatchupWrapper>
                <TeamWrapper className={'away'}>
                    <Team
                        nameFontSize={ nameFontSize }
                        alt={awayData?.team?.abbreviation}
                        logoSize={logoSize}
                        teamLogoType={ SPOT }
                        leftAligned={ false }
                        name={awayData?.team?.abbreviation }
                        wins={ awayData?.leagueRecord?.wins }
                        losses={awayData?.leagueRecord?.losses }
                        teamId={ awayData?.team?.id}
                        displayRecord={true}
                        isFavorite={awayData?.isFavorite}
                        isLoading={isLoading}
                    />
                </TeamWrapper>
                <ScoreWrapper>
                    <TeamScoreWrapper>{!isLoading && gameState !== 'Preview' ? awayScore : null}</TeamScoreWrapper>
                    <GameInfoWrapper>
                        {!isLoading
                            ? <InfoWrapper>
                                <div>{gameBaseLabel}</div>
                                { gameSubLabel && <SubLabelWrapper>{gameSubLabel}</SubLabelWrapper>}
                                { enabledInningState && <GameDataLayer
                                    dictionary={ dictionary }
                                    linescore={ linescore }
                                    basepathsWidth={ basepathsWidth }
                                    outsWidth= { outsWidth }
                                    mode={ mode }
                                    isActiveGame={isActiveGame}
                                    env={env}
                                />
                                }
                            </InfoWrapper>
                            : <Skeleton height='10px' width='70px' margin />}
                    </GameInfoWrapper>
                    <TeamScoreWrapper>{!isLoading && gameState !== 'Preview' ? homeScore : null}</TeamScoreWrapper>
                </ScoreWrapper>
                <TeamWrapper className={'home'}>
                    <Team
                        nameFontSize={ nameFontSize }
                        alt={homeData?.team?.abbreviation}
                        logoSize={logoSize}
                        teamLogoType={ SPOT }
                        leftAligned={ true }
                        name={homeData?.team?.abbreviation }
                        wins={ homeData?.leagueRecord?.wins }
                        losses={homeData?.leagueRecord?.losses }
                        teamId={ homeData?.team?.id}
                        displayRecord={true}
                        isFavorite={homeData?.isFavorite}
                        isLoading={isLoading}
                    />
                </TeamWrapper>
            </TeamMatchupWrapper>
        </ContentWrapperOverride>
    );
};

VerticalGame.propTypes = {
    disableCell: PropTypes.bool,
    flags: PropTypes.bool,
    dictionary: PropTypes.object,
    mode: PropTypes.string,
    status: PropTypes.object,
    teams: PropTypes.object,
    linescore: PropTypes.object,
    gameState: PropTypes.string,
    gameDate: PropTypes.string,
    gameNumber: PropTypes.string,
    isLoading: PropTypes.bool,
    enabledInningState: PropTypes.bool,
    outsWidth: PropTypes.number,
    basepathsWidth: PropTypes.number,
    isActiveGame: PropTypes.bool,
    tz: PropTypes.string,
    env: PropTypes.string
};

export default VerticalGame;
